import React from 'react';

export interface ColumnConfig {
    key: string;
    label: string;
    sortable?: boolean;
    render?: (row: any) => React.ReactNode;
}

interface SortConfig {
    key: string | null;
    direction: 'asc' | 'desc' | null;
}

interface Table {
    t: (key: string) => string;
    titleKey: string;
    placeholder: string;
    searchTerm: string;
    setSearchTerm: (v: string) => void;
    handleSearch: () => void;
    downloadDataAsXLSX: () => void;
    isFullScreen: boolean;
    setIsFullScreen: (v: boolean) => void;

    nameClassTable: string;
    columns: ColumnConfig[];
    currentData: any[];
    sortConfig: SortConfig;
    onSort: (key: any) => void;
    onRowEdit?: (row: any) => void;
    onRowDelete?: (row: any) => void;

    handlePreviousPage: () => void;
    currentPage: number,
    totalPage: number,
    itemsPerPage: number,
    filteredData: any[],
    setItemsPerPage: React.Dispatch<React.SetStateAction<number>>
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
    handleNextPage: () => void;
    data: any[];
}

const Table: React.FC<Table> = ({
    t,
    titleKey,
    placeholder,
    searchTerm,
    setSearchTerm,
    handleSearch,
    downloadDataAsXLSX,
    isFullScreen,
    setIsFullScreen,

    nameClassTable,
    columns,
    currentData,
    sortConfig,
    onSort,
    onRowEdit,
    onRowDelete,

    handlePreviousPage,
    currentPage,
    totalPage,
    itemsPerPage,
    filteredData,
    setItemsPerPage,
    setCurrentPage,
    handleNextPage,
    data,
}) => {
    const getValue = (obj: any, path: string): any => {
        return path.split(".").reduce((acc, part) => acc?.[part], obj);
    };

    return (
        <>
            <div className="caption">
                <div className="left">
                    <h5>{t(titleKey)}</h5>
                </div>
                <div className="right">
                    <button className="btn search-btn" onClick={handleSearch}>
                        <i className="fas fa-search"></i>
                    </button>

                    <input
                        type="text"
                        placeholder={t(placeholder) + ' ...'}
                        className="form-control search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                    />

                    <button className="btn btn-dark download-btn" onClick={downloadDataAsXLSX}>
                        <i className="fas fa-download"></i>
                    </button>

                    <button
                        className="btn btn-dark fullscreen-btn"
                        onClick={() => setIsFullScreen(!isFullScreen)}
                    >
                        <i className={`fas ${isFullScreen ? 'fa-compress' : 'fa-expand'}`}></i>
                    </button>
                </div>
            </div>

            <div className={nameClassTable}>
                <table className="table table-hover">
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    onClick={() => col.sortable && onSort(col.key)}
                                    style={{ cursor: col.sortable ? 'pointer' : 'default' }}
                                >
                                    {col.label}{' '}
                                    {col.sortable &&
                                        (sortConfig.key === col.key ? (
                                            sortConfig.direction === 'asc' ? (
                                                <i className="fas fa-arrow-up" />
                                            ) : (
                                                <i className="fas fa-arrow-down" />
                                            )
                                        ) : (
                                            <i className="fa-solid fa-arrow-down-a-z"></i>
                                        ))}
                                </th>
                            ))}
                            {onRowEdit && <th>{t('incidents.actions')}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {currentData.length > 0 ? (
                            currentData.map((row, index) => (
                                <tr key={index}>
                                    {columns.map((col) => (
                                        <td key={col.key}>
                                            {col.render ? col.render(row) : getValue(row, col.key) ?? ""}
                                        </td>
                                    ))}
                                    {(onRowEdit || onRowDelete) && (
                                        <td className="actions">
                                            {onRowEdit && (
                                                <i onClick={() => onRowEdit(row)}
                                                    className="fas fa-edit"></i>
                                            )}
                                            {onRowDelete && (
                                                <i onClick={() => onRowDelete(row)}
                                                    className="fas fa-trash"></i>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length + (onRowEdit ? 1 : 0)} style={{ textAlign: 'center' }}>
                                    {t('incidents.noInformationAvailable')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="pagination">
                <button className="btn btn-secondary" onClick={handlePreviousPage} disabled={currentPage === 1}>
                    <i className="fas fa-chevron-left"></i> {t('incidents.previous')}
                </button>
                <div>
                    <span>{t('incidents.page')} {currentPage} {t('incidents.of')} {totalPage},</span>
                    <div className="pagination-controls">
                        <span>{t('incidents.rowsPerPage')}:</span>
                        <select
                            className="form-control rows-select"
                            value={
                                itemsPerPage === filteredData.length && ![5, 10, 15, 50].includes(itemsPerPage)
                                    ? "All"
                                    : itemsPerPage
                            }
                            onChange={(e) => {
                                const value = e.target.value === "All" ? filteredData.length : parseInt(e.target.value, 10);
                                setItemsPerPage(value);
                                setCurrentPage(1);
                            }}
                        >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="15">15</option>
                            <option value="50">50</option>
                            <option value="All">{t('incidents.all')}</option>
                        </select>
                    </div>
                </div>
                <button
                    className="btn btn-secondary"
                    onClick={handleNextPage}
                    disabled={currentPage === Math.ceil(data.length / itemsPerPage)}
                >
                    {t('incidents.next')} <i className="fas fa-chevron-right"></i>
                </button>
            </div>
        </>
    );
};

export default Table;
